/**
 * DPR — Iter 158 multi-department collective report.
 *
 * One DPR per (project, date). Each required department fills its own
 * section; the page shows a checklist of who's done vs pending. The
 * Finalise button is gated until every required department has filled.
 *
 * Manpower is auto-pulled from attendance (read-only, server-computed).
 * Every execution activity must reference a WBS activity AND a BOQ item.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Routes, Route, NavLink, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Circle, AlertTriangle, Users, Lock, Hammer, ClipboardCheck, ShieldAlert, Boxes, Truck, Compass, CloudOff, Send, Download, BellRing } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { apiPost, apiDownload } from '../lib/api';
import { formatDateTime as _fmtDT } from '../lib/format';
import { cachedGet } from '../lib/offlineQueue';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import { toast } from 'sonner';
import { WeatherStrip, SitePhotos, HindrancesPanel, VisitorsPanel } from '../components/DprExtras';
import { useAuth } from '../contexts/AuthContext';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from '../components/ui/alert-dialog';
import { SectionEditor } from './dpr/editors/SectionEditor';
import ProgressReport from './dpr/ProgressReport';

const STATUS_BADGE = {
  draft: { label: 'Draft', cls: 'text-slate-600 bg-slate-50 border-slate-200' },
  in_progress: { label: 'In Progress', cls: 'text-amber-700 bg-amber-50 border-amber-200' },
  ready: { label: 'Ready', cls: 'text-sky-700 bg-sky-50 border-sky-200' },
  pending_pm_review: { label: 'Pending PM Review', cls: 'text-violet-700 bg-violet-50 border-violet-200' },
  complete: { label: 'Finalised', cls: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
};
// Re-export the legacy single-shot form so /m/dpr (MobileDPR.jsx) and any
// other consumers that imported `{ DPRForm }` from `pages/DPR` keep working
// while the multi-dept rollout is in progress. The legacy form posts to
// POST /dpr which now writes into the execution section.
export { DPRForm } from './DPR.legacy';

const DEPT_ICON = {
  execution: Hammer,
  planning: Compass,
  qa_qc: ClipboardCheck,
  safety: ShieldAlert,
  stores: Boxes,
  procurement: Truck,
};

export function DprDailyConsole() {
  const { user } = useAuth() || {};
  const { online } = useNetworkStatus();
  const [searchParams] = useSearchParams();
  const today = new Date().toISOString().slice(0, 10);
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState(searchParams.get('project_id') || '');
  const [date, setDate] = useState(searchParams.get('date') || today);
  const [doc, setDoc] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeDept, setActiveDept] = useState(null);
  const [authority, setAuthority] = useState(null); // { authority, can_finalise, editable_departments }
  const [reminding, setReminding] = useState(false);
  // Deep-link support (?dept=) — a URL dept wins over the default exactly
  // once; cleared after use so manual tab clicks aren't fought.
  const deepLinkDeptRef = useRef(searchParams.get('dept') || null);
  // Once the viewer manually clicks a tile, the default-tab effect below
  // must never override that choice again for this doc/session.
  const userPickedDeptRef = useRef(false);

  useEffect(() => {
    cachedGet('projects', '/projects').then(({ data }) => {
      const list = Array.isArray(data) ? data : (data?.projects || []);
      setProjects(list);
      if (!projectId && list[0]) setProjectId(list[0].id);
    }).catch(() => {});
  }, []);  // eslint-disable-line

  // FIX 7c/7d — real, project-scoped authority (mirrors backend _dpr_authority exactly),
  // replacing the old client-side role-string-only isDprManager() guess.
  // Cache-then-network so authority is still known offline once fetched once.
  useEffect(() => {
    if (!projectId) { setAuthority(null); return; }
    cachedGet(`dpr-authority-${projectId}`, `/dpr/${projectId}/my-authority`).then(({ data }) => setAuthority(data)).catch(() => setAuthority(null));
  }, [projectId]);

  const canManage = authority?.can_finalise || false;
  const canEditDept = (dept) => authority?.authority === 'full' || authority?.editable_departments?.includes(dept);

  const load = async () => {
    if (!projectId || !date) return;
    setLoading(true);
    try {
      const [dRes, mRes] = await Promise.all([
        cachedGet(`dpr-doc-${projectId}-${date}`, `/dpr/by-date?project_id=${encodeURIComponent(projectId)}&date=${encodeURIComponent(date)}`),
        cachedGet(`dpr-meta-${projectId}-${date}`, `/dpr-meta?project_id=${encodeURIComponent(projectId)}&date=${encodeURIComponent(date)}`),
      ]);
      const d = dRes.data;
      const m = mRes.data;
      setDoc(d);
      setMeta(m);
    } catch (e) {
      toast.error('Failed to load DPR');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId, date]);

  // D3a — default active tile: a ?dept= deep link always wins (once);
  // otherwise default to the viewer's OWN editable department if it's part
  // of today's required set, falling back to the server's first required
  // department only when the viewer has no matching editable department
  // (e.g. company-wide roles like MD/super_admin). Re-runs whenever doc or
  // authority change so a slower authority fetch still corrects an initial
  // guess — but never overrides a tile the user has manually clicked.
  useEffect(() => {
    const required = doc?.completion?.required || [];
    if (!required.length) return;
    if (deepLinkDeptRef.current && required.includes(deepLinkDeptRef.current)) {
      setActiveDept(deepLinkDeptRef.current);
      deepLinkDeptRef.current = null;
      return;
    }
    if (userPickedDeptRef.current && activeDept) return;
    const myEditable = (authority?.editable_departments || []).filter((d2) => required.includes(d2));
    const preferred = myEditable[0] || required[0];
    if (activeDept !== preferred) setActiveDept(preferred);
  }, [doc, authority]);  // eslint-disable-line react-hooks/exhaustive-deps

  const required = doc?.completion?.required || [];
  const status = doc?.status || 'draft';
  const isFinalised = status === 'complete';
  const isSubmitted = status === 'pending_pm_review';
  const allDone = doc?.completion?.is_complete;
  const badge = STATUS_BADGE[status] || STATUS_BADGE.draft;

  const finalise = async () => {
    if (!doc?.id) return;
    try {
      const res = await apiPost(`/dpr/${doc.id}/finalise`, {});
      setDoc(res);
      toast.success('DPR finalised — sections are now locked.');
    } catch (e) {
      const det = e?.response?.data?.detail;
      const pending = det?.pending?.map((p) => p.label).join(', ');
      toast.error(pending ? `Pending: ${pending}` : (det?.message || det || 'Could not finalise'));
    }
  };

  const submitForReview = async () => {
    if (!doc?.id) return;
    await doSubmitForReview();
  };

  const doSubmitForReview = async () => {
    if (!doc?.id) return;
    try {
      const res = await apiPost(`/dpr/${doc.id}/submit-for-review`, {});
      setDoc(res);
      toast.success('Submitted for PM review.');
    } catch (e) {
      const det = e?.response?.data?.detail;
      const pending = det?.pending?.map((p) => p.label).join(', ');
      toast.error(pending ? `Pending: ${pending}` : (det?.message || det || 'Could not submit'));
    }
  };

  const remind = async () => {
    if (!doc?.id) return;
    setReminding(true);
    try {
      const res = await apiPost(`/dpr/${doc.id}/remind`, {});
      toast.success(`Reminder sent to ${(res.sent_to || []).join(', ') || 'the PM'}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send reminder');
    } finally {
      setReminding(false);
    }
  };

  // A section save that hit no network gets queued (see postSection/saveSection)
  // and comes back here tagged `__offline_queued` instead of a fresh doc — merge
  // it into local state so the tile flips to "filled" immediately, on-site.
  const handleSectionSaved = (updated) => {
    if (updated?.__offline_queued) {
      setDoc((prev) => prev ? {
        ...prev,
        department_sections: {
          ...prev.department_sections,
          [updated.dept]: {
            ...(prev.department_sections?.[updated.dept] || {}),
            data: updated.data,
            filled: true,
            filled_by: user?.email,
            queued_offline: true,
          },
        },
      } : prev);
      return;
    }
    setDoc(updated);
  };

  return (
    <div data-testid="dpr-page">
      <PageHeader
        overline="Project Progress Reports"
        title="DPR · Collective Daily Report"
        subtitle="One DPR per project per day, filled together by every department. Manpower auto-synced from attendance."
      />

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-sm p-3 mb-4 flex flex-wrap items-end gap-3" data-testid="dpr-filters">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-1">Project</label>
          <select value={projectId} onChange={(e) => setProjectId(e.target.value)} className="border border-slate-200 rounded-sm text-sm px-3 py-2 bg-white min-w-[240px]" data-testid="dpr-project">
            <option value="">— Select —</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-1">Date</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="border border-slate-200 rounded-sm text-sm px-3 py-2" data-testid="dpr-date" />
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {doc && (
            <span className={`inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-semibold border px-2 py-1 rounded-sm ${badge.cls}`} data-testid="dpr-status-badge">
              {isFinalised && <Lock size={12} />} {badge.label}
            </span>
          )}
          {/* Submit for PM review — any department member, once all sections are filled */}
          {!isFinalised && !isSubmitted && (
            <button
              onClick={submitForReview}
              disabled={!doc?.id || !allDone}
              className="inline-flex items-center gap-1.5 text-sm font-display font-semibold uppercase tracking-wider px-4 py-2 rounded-sm bg-violet-600 text-white disabled:bg-slate-200 disabled:text-slate-400"
              data-testid="dpr-submit-review-btn"
              title={allDone ? 'Submit this DPR for PM review' : 'Fill every department section before submitting'}
            >
              <Send size={13} /> {allDone ? 'Submit for PM Review' : 'Submit (pending)'}
            </button>
          )}
          {/* Download PDF — only available for finalised DPRs */}
          {isFinalised && doc?.id && (
            <button
              onClick={() => {
                const proj = projects.find(p => p.id === projectId);
                const fname = `DPR_${proj?.code || proj?.name || projectId}_${date}.pdf`;
                apiDownload(`/dpr/${doc.id}/report.pdf`, fname).catch(e => toast.error('PDF generation failed: ' + (e?.message || 'Unknown error')));
              }}
              className="inline-flex items-center gap-1.5 text-sm font-display font-semibold uppercase tracking-wider px-3 py-2 rounded-sm border border-slate-300 text-slate-700 hover:bg-slate-50"
              data-testid="dpr-download-pdf-btn"
              title="Download this DPR as a formatted PDF"
            >
              <Download size={13} /> Download PDF
            </button>
          )}
          {/* Remind — nudge the PM with an email + bell reminder for a DPR stuck in review */}
          {isSubmitted && !isFinalised && (
            <button
              onClick={remind}
              disabled={reminding || !doc?.id}
              className="inline-flex items-center gap-1.5 text-sm font-display font-semibold uppercase tracking-wider px-3 py-2 rounded-sm border border-violet-300 text-violet-700 hover:bg-violet-50 disabled:opacity-50"
              data-testid="dpr-remind-btn"
              title="Nudge the PM with a reminder email + bell notification"
            >
              <BellRing size={13} /> {reminding ? 'Sending…' : 'Remind PM to review'}
            </button>
          )}
          {/* Finalise (lock) — PM / Project Director / exec only */}
          {!isFinalised && (
            <button
              onClick={finalise}
              disabled={!doc?.id || !allDone || !canManage}
              className="text-sm font-display font-semibold uppercase tracking-wider px-4 py-2 rounded-sm bg-[#3b4872] text-white disabled:bg-slate-200 disabled:text-slate-400"
              data-testid="dpr-finalise-btn"
              title={!canManage ? 'Only the PM / Project Director can approve & finalise the DPR' : (allDone ? 'Approve & finalise this DPR' : `Pending: ${(doc?.completion?.pending || []).map(p => p.label).join(', ')}`)}
            >
              {canManage ? (allDone ? 'Approve & Finalise →' : 'Finalise (pending)') : 'PM-only Finalise'}
            </button>
          )}
        </div>
      </div>

      {/* Offline-sync audit — surfaces when a DPR was authored offline on-site
          and only reached the server later (distinguishes real-time from backdated). */}
      {doc?.offline_queued_at && (
        <div data-testid="dpr-offline-audit" className="mb-4 flex items-center gap-2 text-[12px] bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 rounded-sm">
          <CloudOff size={13} /> Filed offline on-site at <b>{_fmtDT(doc.offline_queued_at)}</b> — synced later.
        </div>
      )}

      {/* Live connectivity notice — section saves below still work: they queue
          on-device and sync automatically the moment you're back online. */}
      {!online && (
        <div data-testid="dpr-network-offline-banner" className="mb-4 flex items-center gap-2 text-[12px] bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 rounded-sm">
          <CloudOff size={13} /> You're offline. Section saves will be queued on this device and synced automatically when you reconnect.
        </div>
      )}

      {/* Completion strip */}
      {doc && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-4 sticky top-0 z-10 bg-[#FDFDFD] py-2" data-testid="dpr-dept-strip">
          {required.map((dept) => {
            const sec = doc.department_sections?.[dept];
            const done = !!sec?.filled;
            const editable = canEditDept(dept);
            const Icon = DEPT_ICON[dept] || Circle;
            return (
              <button
                key={dept}
                onClick={() => { userPickedDeptRef.current = true; setActiveDept(dept); }}
                data-testid={`dpr-dept-${dept}`}
                className={`text-left bg-white border rounded-sm p-3 transition ${
                  activeDept === dept ? 'border-[#3b4872]' : 'border-slate-200'
                } ${done ? 'ring-1 ring-emerald-200' : ''} ${!editable ? 'opacity-70' : ''}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <Icon size={14} className={done ? 'text-emerald-600' : 'text-slate-400'} />
                  <div className="flex items-center gap-1">
                    {!editable && <Lock size={11} className="text-slate-400" data-testid={`dpr-dept-${dept}-locked`} />}
                    {done ? <CheckCircle2 size={14} className="text-emerald-600" /> : <Circle size={14} className="text-slate-300" />}
                  </div>
                </div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{meta?.department_labels?.[dept] || dept}</div>
                <div className="text-[10px] text-slate-400 mt-0.5" data-testid={`dpr-dept-${dept}-status`}>
                  {done ? `Filled · ${sec.queued_offline ? 'queued offline' : (sec.filled_by?.split('@')[0] || '')}` : (editable ? 'Pending' : 'View only')}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Manpower (read-only) */}
      {doc?.manpower && (() => {
        const mp = doc.manpower;
        const isEmpty = (mp.total_present_attendance || 0) === 0 && (mp.skill_total || 0) === 0;
        const buckets = Object.entries(mp.skill_counts || {}).filter(([, v]) => v > 0);
        if (isEmpty) {
          return (
            <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 mb-4 flex items-start gap-2 text-xs text-amber-800" data-testid="dpr-manpower-empty">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <div>
                <div className="font-display font-bold uppercase tracking-wider">No attendance data for this date</div>
                <div className="mt-0.5">Attendance feeds automatically from the face-recognition kiosk. Ensure the kiosk is active and workers have been enrolled at this site.</div>
              </div>
            </div>
          );
        }
        return (
          <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 mb-4 flex items-center gap-3" data-testid="dpr-manpower">
            <Users size={18} className="text-slate-500" />
            <div className="text-xs">
              <div className="font-display font-bold uppercase tracking-wider text-slate-700">
                Manpower · Auto-synced from attendance
                <Lock size={10} className="inline ml-1 text-slate-400" />
              </div>
              <div className="text-slate-600">
                Total present: <b data-testid="dpr-manpower-total">{mp.total_present_attendance}</b>
                {buckets.length > 0 && <> · {buckets.map(([k, v]) => `${k}: ${v}`).join(', ')}</>}
              </div>
            </div>
          </div>
        );
      })()}

      {loading && <div className="text-center py-12 text-sm text-slate-400">Loading…</div>}
      {!loading && !projectId && (
        <div className="text-center py-12 text-sm text-slate-500">Select a project to start the DPR for {date}.</div>
      )}

      {/* FIX 6 — daily weather header. D1 (13-Sep-2026): scoped to Execution
          and Safety only — the two departments that actually act on weather
          (pour/casting go-no-go; outdoor-work risk & toolbox-talk conditions).
          Quality/Stores/Procurement don't need it in their section. */}
      {doc && projectId && ['execution', 'safety'].includes(activeDept) && (
        <WeatherStrip key={`${doc?.id || 'new'}-${date}`} doc={doc} projectId={projectId} date={date} locked={isFinalised} onSaved={setDoc} />
      )}

      {/* FIX 7 — site photos */}
      {doc?.id && <SitePhotos doc={doc} locked={isFinalised} onChanged={load} />}

      {/* Active section editor — editable for your own department */}
      {doc && activeDept && !isFinalised && authority && canEditDept(activeDept) && (
        <SectionEditor
          key={`${doc.id || 'new'}-${activeDept}`}
          dept={activeDept}
          deptLabel={meta?.department_labels?.[activeDept] || activeDept}
          section={doc.department_sections?.[activeDept] || null}
          meta={meta}
          onSaved={handleSectionSaved}
          projectId={projectId}
          date={date}
        />
      )}

      {/* G.1 — another department's section: read-only view (inputs disabled,
          Save hidden) instead of a bare "no access" banner, so a PM or any
          other department can still see exactly what was reported. */}
      {doc && activeDept && !isFinalised && authority && !canEditDept(activeDept) && (
        <SectionEditor
          key={`${doc.id || 'new'}-${activeDept}-ro`}
          dept={activeDept}
          deptLabel={meta?.department_labels?.[activeDept] || activeDept}
          section={doc.department_sections?.[activeDept] || null}
          meta={meta}
          onSaved={(updated) => setDoc(updated)}
          projectId={projectId}
          date={date}
          readOnly
        />
      )}

      {doc && activeDept && isFinalised && (
        <>
          <div className="bg-white border border-slate-200 rounded-sm p-4 mb-3 text-sm text-slate-500" data-testid="dpr-locked-banner">
            <Lock className="inline mr-1" size={14} /> This DPR has been finalised. All sections are locked.
          </div>
          <SectionEditor
            key={`${doc.id || 'new'}-${activeDept}-final`}
            dept={activeDept}
            deptLabel={meta?.department_labels?.[activeDept] || activeDept}
            section={doc.department_sections?.[activeDept] || null}
            meta={meta}
            onSaved={(updated) => setDoc(updated)}
            projectId={projectId}
            date={date}
            readOnly
          />
        </>
      )}

      {/* FIX 8 / FIX 12 — read-only hindrance + visitor logs pulled into the DPR */}
      {doc && (
        <div className="mt-4">
          <HindrancesPanel doc={doc} />
          <VisitorsPanel doc={doc} />
        </div>
      )}

    </div>
  );
}

const TAB_CLS = ({ isActive }) =>
  `px-3 py-1.5 text-[11px] font-display font-bold uppercase tracking-wider rounded-sm transition-colors ${
    isActive ? 'bg-white text-[#3b4872] shadow-sm' : 'text-slate-500 hover:text-slate-700'
  }`;

export default function DPR() {
  return (
    <div>
      <div className="flex items-center gap-1 mb-4 bg-slate-100 rounded-sm p-0.5 border border-slate-200 w-fit" data-testid="dpr-tab-switcher">
        <NavLink to="/app/dpr" end className={TAB_CLS} data-testid="dpr-tab-daily-report">Daily Report</NavLink>
        <NavLink to="/app/dpr/progress" className={TAB_CLS} data-testid="dpr-tab-progress-reports">Progress Reports</NavLink>
      </div>
      <Routes>
        <Route index element={<DprDailyConsole />} />
        <Route path="progress" element={<ProgressReport />} />
      </Routes>
    </div>
  );
}

